import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthPolicy } from '../../../../core/enums/auth-policy';
import { EditablePostClientStatus } from '../../../../core/enums/editable-post-client-status';
import { MachineType } from '../../../../core/enums/machine-type';
import { MachinePerformanceDto } from '../../../../core/models/machines/machine-performance-dto';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-machine-performance',
  imports: [MatTableModule, MatPaginatorModule, CommonModule,
    MatTabsModule, FormsModule, MatFormField, MatInputModule,
    MatCheckboxModule, MatButtonModule, MatTooltipModule],
  templateUrl: './machine-performance.component.html',
  styleUrl: './machine-performance.component.scss'
})

export class MachinePerformanceComponent {
  @Input() regenerationPerformance: MachinePerformanceDto[] = [];
  @Input() thinningPerformance: MachinePerformanceDto[] = [];
  @Input() machineType: MachineType | null = null;

  @Output() machineChanged = new EventEmitter<boolean>();

  newRowDefaultValues = {
    meanStemVolumeInM3FubPerTree: undefined,
    g5InM3FubPerHour: undefined,
    clientStatus: EditablePostClientStatus.New,
    transportDistanceForwarding: undefined
  };

  thinningPerformanceDataSource = new MatTableDataSource<MachinePerformanceDto>;
  regenerationPerformanceDataSource = new MatTableDataSource<MachinePerformanceDto>;
  currentPerformanceDataSource = new MatTableDataSource<MachinePerformanceDto>;
  displayedColumns = ["g5InM3FubPerHour-c"];
  clientStatus = EditablePostClientStatus;
  newRow: MachinePerformanceDto = { ...this.newRowDefaultValues };
  isPolicyPt: boolean = false;
  isReadonly: boolean = true;

  constructor(public authService: AuthService) {
  }

  ngOnInit() {
    this.isPolicyPt = this.authService.isPolicy(AuthPolicy.PT);
    if (this.isPolicyPt) {
      this.displayedColumns.push("action-c");
    }
    this.isReadonly = !this.authService.isPolicyPT();
    this.currentPerformanceDataSource = this.thinningPerformanceDataSource;
  }

  private sortPerformances() {
    if (this.machineType === MachineType.Harvester) {
      this.regenerationPerformance.sort((a, b) => (a.meanStemVolumeInM3FubPerTree! > b.meanStemVolumeInM3FubPerTree! ? 1 : -1));
      this.thinningPerformance.sort((a, b) => (a.meanStemVolumeInM3FubPerTree! > b.meanStemVolumeInM3FubPerTree! ? 1 : -1));
    }

    if (this.machineType === MachineType.Forwarder) {
      this.regenerationPerformance.sort((a, b) => (a.transportDistanceForwarding! > b.transportDistanceForwarding! ? 1 : -1));
      this.thinningPerformance.sort((a, b) => (a.transportDistanceForwarding! > b.transportDistanceForwarding! ? 1 : -1));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['thinningPerformance'] && this.thinningPerformanceDataSource) {
      this.thinningPerformanceDataSource.data = this.thinningPerformance;
      this.sortPerformances();
      this.currentPerformanceDataSource._updateChangeSubscription();
    }

    if (changes['regenerationPerformance'] && this.regenerationPerformance) {
      this.regenerationPerformanceDataSource.data = this.regenerationPerformance;
      this.sortPerformances();
      this.currentPerformanceDataSource._updateChangeSubscription();
    }

    if (changes['machineType']) {
      this.initCurrentMachinesDisplayedColumns();
    }
  }

  selectedTabChange(event: MatTabChangeEvent) {
    const label = event.tab.textLabel;
    this.currentPerformanceDataSource = label === "Gallring" ? this.thinningPerformanceDataSource : this.regenerationPerformanceDataSource
  }

  onRowChanged(row: MachinePerformanceDto) {
    if (row.id) {
      row.clientStatus = EditablePostClientStatus.Updated;
      this.machineChanged.emit(true);
    }
  }

  removeRow(row: MachinePerformanceDto, index: any) {
    if (row.id) {
      row.clientStatus = EditablePostClientStatus.Deleted;
      this.machineChanged.emit(true);
    } else {
      this.currentPerformanceDataSource.data.splice(index, 1);
    }

    this.currentPerformanceDataSource._updateChangeSubscription();
  }

  addNewRow() {
    this.currentPerformanceDataSource.data.push(this.newRow);
    this.currentPerformanceDataSource._updateChangeSubscription();
    this.clearNewRow();
    this.machineChanged.emit(true);
  }

  clearNewRow() {
    this.newRow = { ...this.newRowDefaultValues };
  }

  newRowIsEmpty(): boolean {
    if (this.machineType === MachineType.Harvester && (!this.newRow.g5InM3FubPerHour || !this.newRow.meanStemVolumeInM3FubPerTree)) {
      return true;
    }

    if (this.machineType === MachineType.Harvester && (this.newRow.g5InM3FubPerHour === 0 && this.newRow.meanStemVolumeInM3FubPerTree === 0)) {
      return true;
    }

    if (this.machineType === MachineType.Forwarder && (!this.newRow.transportDistanceForwarding || !this.newRow.g5InM3FubPerHour)) {
      return true;
    }

    if (this.machineType === MachineType.Forwarder && (this.newRow.transportDistanceForwarding === 0 && this.newRow.g5InM3FubPerHour === 0)) {
      return true;
    }

    return false;
  }

  initCurrentMachinesDisplayedColumns() {
    if (this.machineType === MachineType.Harvester) {
      this.displayedColumns.unshift("meanStemVolumeInM3FubPerTree-c");
    }

    if (this.machineType === MachineType.Forwarder) {
      this.displayedColumns.unshift("transportDistanceForwarding-c");
    }
  }
}

